/**
 * BW20-C — circular Consumed / Burned / Target gauge for /log/meals.
 *
 * Single SVG, no charting library. Center reads kcal remaining (target +
 * burned - consumed). Three arcs: consumed (filled, primary), burned (filled,
 * secondary, drawn underneath consumed so consumed visually dominates),
 * target (track, light gray). On a Lifesum-style day, the consumed arc grows
 * clockwise from 12 o'clock; the burned arc grows counter-clockwise from
 * 12 o'clock so the two never overlap visually.
 *
 * Accessibility: the SVG has role="img" and an aria-label with the same
 * numbers the gauge renders, so a screen reader gets the full picture.
 */

import { useMemo } from "react";

export type DailyDiaryGaugeProps = {
  consumed: number;
  burned: number;
  target: number;
  size?: number;
};

const STROKE = 18;
const VIEW = 220;

export function DailyDiaryGauge({
  consumed,
  burned,
  target,
  size = VIEW,
}: DailyDiaryGaugeProps) {
  const remaining = Math.round(target + burned - consumed);

  const radius = (VIEW - STROKE) / 2;
  const cx = VIEW / 2;
  const cy = VIEW / 2;
  const circumference = 2 * Math.PI * radius;

  // Fractions clamp to [0, 1.05] so a slight overshoot is still visible
  // (no full ring overflow).
  const consumedFrac = Math.max(0, Math.min(1.05, consumed / Math.max(1, target)));
  const burnedFrac = Math.max(0, Math.min(0.6, burned / Math.max(1, target)));

  // Color the inner number by remaining-vs-target risk. On a fueling day,
  // remaining > 0 = green; remaining within ±10% of zero = amber; remaining
  // significantly < 0 (overshoot) = brick. Under-fueling is shown by the
  // bottom-row Consumed value below the gauge, not by recoloring this number.
  const tone = useMemo(() => {
    const drift = remaining / Math.max(1, target);
    if (drift > 0.05) return "var(--green-700, #2f8a3a)";
    if (drift > -0.1) return "var(--amber-700, #b07b00)";
    return "var(--brick, #b04a30)";
  }, [remaining, target]);

  // Consumed arc: starts at 12 o'clock (rotation -90deg), grows clockwise.
  const consumedDash = `${consumedFrac * circumference} ${circumference}`;

  // Burned arc: starts at 12 o'clock, grows counter-clockwise. Achieved by
  // negative dasharray + reversed transform.
  const burnedDash = `${burnedFrac * circumference} ${circumference}`;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${VIEW} ${VIEW}`}
      role="img"
      aria-label={`${remaining} kcal remaining. Consumed ${Math.round(
        consumed,
      )}. Burned ${Math.round(burned)}. Target ${Math.round(target)}.`}
    >
      {/* Track */}
      <circle
        cx={cx}
        cy={cy}
        r={radius}
        fill="none"
        stroke="var(--bg-2, #ececec)"
        strokeWidth={STROKE}
      />
      {/* Burned arc — counter-clockwise from 12 o'clock */}
      <circle
        cx={cx}
        cy={cy}
        r={radius}
        fill="none"
        stroke="var(--haus-amber-500, #d99a3a)"
        strokeWidth={STROKE}
        strokeLinecap="round"
        strokeDasharray={burnedDash}
        transform={`rotate(-90 ${cx} ${cy}) scale(-1 1) translate(${-VIEW} 0)`}
        opacity={0.85}
      />
      {/* Consumed arc — clockwise from 12 o'clock */}
      <circle
        cx={cx}
        cy={cy}
        r={radius}
        fill="none"
        stroke="var(--haus-green-600, #4a9c52)"
        strokeWidth={STROKE}
        strokeLinecap="round"
        strokeDasharray={consumedDash}
        transform={`rotate(-90 ${cx} ${cy})`}
      />

      {/* Center label */}
      <text
        x={cx}
        y={cy - 6}
        textAnchor="middle"
        fontFamily="var(--font-serif)"
        fontStyle="italic"
        fontSize={36}
        fontWeight={500}
        fill={tone}
      >
        {remaining}
      </text>
      <text
        x={cx}
        y={cy + 18}
        textAnchor="middle"
        fontFamily="var(--font-sans)"
        fontSize={11}
        letterSpacing="0.08em"
        fill="var(--fg-3)"
        style={{ textTransform: "uppercase" }}
      >
        kcal remaining
      </text>
    </svg>
  );
}

export function DailyDiaryGaugeLegend({
  consumed,
  burned,
  target,
}: {
  consumed: number;
  burned: number;
  target: number;
}) {
  return (
    <ul
      style={{
        listStyle: "none",
        padding: 0,
        margin: 0,
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gap: "var(--space-3)",
        textAlign: "center",
      }}
    >
      <LegendItem
        swatch="var(--haus-green-600, #4a9c52)"
        label="Consumed"
        value={Math.round(consumed)}
      />
      <LegendItem
        swatch="var(--haus-amber-500, #d99a3a)"
        label="Burned"
        value={Math.round(burned)}
      />
      <LegendItem swatch="var(--bg-3, #d9d9d9)" label="Target" value={Math.round(target)} />
    </ul>
  );
}

function LegendItem({
  swatch,
  label,
  value,
}: {
  swatch: string;
  label: string;
  value: number;
}) {
  return (
    <li
      style={{
        display: "grid",
        gap: 2,
      }}
    >
      <span
        style={{
          fontFamily: "var(--font-sans)",
          fontSize: "var(--type-micro)",
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "var(--fg-3)",
        }}
      >
        <span
          aria-hidden
          style={{
            display: "inline-block",
            width: 8,
            height: 8,
            borderRadius: 2,
            background: swatch,
            marginRight: 6,
            verticalAlign: "middle",
          }}
        />
        {label}
      </span>
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: "var(--type-body)",
          color: "var(--fg-1)",
        }}
      >
        {value.toLocaleString()}
      </span>
    </li>
  );
}
